import { useState } from 'react';
import type { TelegramEvent, TelegramEventStatus } from '@wave/shared';
import { useTelegramEvents } from '../api/hooks';
import { useApplyTelegramEvent, useDismissTelegramEvent } from '../api/mutations';
import {
  btnDanger,
  btnPrimarySm,
  btnSuccessSm,
  pageContainer,
  pageHeader,
  pageSubtitle,
  pageTitle,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableRow,
  tableWrapper,
} from '../lib/styles';

const STATUS_OPTIONS: { value: TelegramEventStatus | 'all'; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'applied', label: 'Applied' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'failed', label: 'Failed' },
  { value: 'all', label: 'Все' },
];

export function TelegramEventsPage() {
  const [status, setStatus] = useState<TelegramEventStatus | 'all'>('pending');
  const { data: events, isLoading } = useTelegramEvents(
    status === 'all' ? undefined : (status as TelegramEventStatus),
  );

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <div>
          <h1 className={pageTitle}>События Telegram-listener</h1>
          <p className={pageSubtitle}>
            События, созданные при срабатывании regex-правил. Pending — ожидают применения оператором;
            Applied — успешно применены; Dismissed — отклонены; Failed — попытка применения вызвала ошибку.
          </p>
        </div>
      </div>

      <div className="mb-3 flex gap-1">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStatus(opt.value)}
            className={`rounded-md px-3 py-1.5 text-sm ${
              status === opt.value
                ? 'bg-[var(--accent-bg)] font-semibold text-accent'
                : 'text-text2 hover:bg-bg3 hover:text-text'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-text2">Загрузка…</div>
      ) : (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Создано</th>
                <th className={tableHeadCell}>Чат</th>
                <th className={tableHeadCell}>Правило</th>
                <th className={tableHeadCell}>Действие</th>
                <th className={tableHeadCell}>Превью</th>
                <th className={tableHeadCell}>Статус</th>
                <th className={`${tableHeadCell} text-right`}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {(events ?? []).length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-12 text-center text-sm text-text2">
                    Нет событий
                  </td>
                </tr>
              )}
              {(events ?? []).map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', { hour12: false });
}

function EventRow({ event }: { event: TelegramEvent }) {
  const apply = useApplyTelegramEvent();
  const dismiss = useDismissTelegramEvent();
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function doApply() {
    setError(null);
    try {
      await apply.mutateAsync(event.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function doDismiss() {
    setError(null);
    try {
      await dismiss.mutateAsync(event.id);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const previewNote = (event.preview as Record<string, unknown>).note;
  const isPending = event.status === 'pending';

  return (
    <>
      <tr
        className={`${tableRow} cursor-pointer`}
        onClick={() => setExpanded((v) => !v)}
      >
        <td className={`${tableCell} font-mono text-xs text-text2`}>{formatDateTime(event.createdAt)}</td>
        <td className={`${tableCell} text-xs`}>
          <div className="font-mono">{event.chatId}</div>
          {event.chatTitle && <div className="text-text-subtle">{event.chatTitle}</div>}
        </td>
        <td className={tableCell}>
          <div className="font-medium text-text">{event.ruleName}</div>
        </td>
        <td className={tableCell}>
          <span className="rounded border border-[var(--accent-border)] bg-[var(--accent-bg)] px-1.5 py-0.5 text-[11px] font-mono text-accent">
            {event.actionType}
          </span>
        </td>
        <td className={`${tableCell} max-w-md text-sm`}>
          <span className="text-text">{String(previewNote ?? '—')}</span>
        </td>
        <td className={tableCell}>
          <StatusBadge status={event.status} />
        </td>
        <td className={`${tableCell} text-right`} onClick={(e) => e.stopPropagation()}>
          {isPending ? (
            <div className="inline-flex gap-1">
              <button
                onClick={() => void doApply()}
                className={btnSuccessSm}
                disabled={apply.isPending}
              >
                ✓ Применить
              </button>
              <button
                onClick={() => void doDismiss()}
                className={btnDanger}
                disabled={dismiss.isPending}
              >
                ✕ Отклонить
              </button>
            </div>
          ) : (
            <button onClick={() => setExpanded((v) => !v)} className={btnPrimarySm}>
              {expanded ? 'Свернуть' : 'Подробно'}
            </button>
          )}
        </td>
      </tr>
      {(expanded || error) && (
        <tr>
          <td colSpan={7} className="border-b border-border-subtle bg-bg/50 px-3 py-2 text-xs">
            {error && (
              <div className="mb-2 rounded border border-[var(--red-border)] bg-[var(--red-bg)] px-2 py-1 text-danger">
                {error}
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase text-text2">Текст сообщения</div>
                <div className="rounded border border-border bg-bg p-2 font-mono text-[11px] text-text">
                  {event.messageText ?? '—'}
                </div>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase text-text2">Captures</div>
                <pre className="rounded border border-border bg-bg p-2 font-mono text-[11px] text-text">
                  {JSON.stringify(event.captures, null, 2)}
                </pre>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase text-text2">Preview / Result</div>
                <pre className="rounded border border-border bg-bg p-2 font-mono text-[11px] text-text">
                  {JSON.stringify(event.result ?? event.preview, null, 2)}
                </pre>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function StatusBadge({ status }: { status: TelegramEventStatus }) {
  const map = {
    pending: 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent',
    applied: 'border-[var(--green-border)] bg-[var(--green-bg)] text-success',
    dismissed: 'border-border bg-bg3 text-text2',
    failed: 'border-[var(--red-border)] bg-[var(--red-bg)] text-danger',
  };
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${map[status]}`}>
      {status}
    </span>
  );
}
